// Sits above the whole app. Nothing renders until there is a Supabase session,
// which is the piece Retool used to supply and the one genuinely new thing in
// this port.
//
// The gate deliberately checks only "is there a signed-in session". It does NOT
// check whether the email appears in public.cockpit_access. That check belongs
// to resolveCapabilities, runs on the server inside every handler, and fails
// closed to all-false. A signed-in user with no cockpit_access row therefore
// reaches the app and sees empty pages with no edit affordances, which is the
// same thing that happens in Retool today. Duplicating the check here would
// create a second place for the two to disagree.
import { useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase, missingSupabaseConfig } from '../lib/supabase'
import { SignIn } from './SignIn'

// Rendered when the bundle was built without VITE_SUPABASE_URL /
// VITE_SUPABASE_ANON_KEY. See the comment in lib/supabase.ts for why this is a
// screen rather than a module-scope throw.
function MissingConfig() {
  return (
    <div className="flex h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md rounded-xl border border-destructive/40 bg-card p-6 text-sm">
        <p className="font-medium text-foreground">This build has no Supabase configuration.</p>
        <p className="mt-2 text-muted-foreground">
          It was compiled without <code>VITE_SUPABASE_URL</code> and{' '}
          <code>VITE_SUPABASE_ANON_KEY</code>. Create <code>web/.env.local</code> from{' '}
          <code>web/.env.example</code> and run <code>npm run build</code> again.
        </p>
      </div>
    </div>
  )
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    if (missingSupabaseConfig) {
      setChecked(true)
      return
    }
    let active = true
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session)
      setChecked(true)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next)
      setChecked(true)
    })
    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [])

  if (missingSupabaseConfig) return <MissingConfig />

  if (!checked) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Checking your session…</p>
      </div>
    )
  }

  if (!session) return <SignIn />

  // A driver who signed in on /board and then opened the bare address lands
  // here with a Google account the cockpit will refuse on every request. Send
  // him to his own page rather than showing him a cockpit full of refusals.
  if (!isStaffEmail(session.user?.email)) {
    window.location.replace('/board')
    return null
  }

  return <>{children}</>
}

// Mirrors ALLOWED_EMAIL_DOMAINS in the Worker. This is routing, not security:
// the Worker refuses every cockpit route to any other domain regardless.
export function isStaffEmail(email: string | null | undefined): boolean {
  return (email ?? '').toLowerCase().endsWith('@fleetly.co.za')
}
